import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  MapPin, Phone, ArrowRight, CheckCircle, Building2, Star,
  ChevronDown, ChevronUp, ExternalLink, MessageCircle, Shield,
  Home, Layers, Calendar, BadgeCheck, Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = "917977009190";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

// ─── WhatsApp redirect with pre-filled message ───
const waLink = (propertyName) =>
  `https://wa.me/${WHATSAPP}?text=Hi%2C%20I%20am%20interested%20in%20*${encodeURIComponent(propertyName)}*%20listed%20on%20Sneha%20Reality.%20Please%20share%20more%20details%20and%20schedule%20a%20site%20visit.`;

// ─── Status badge colours ───
const statusColor = (status) => {
  if (!status) return "text-[#A0A0A0] border-[#A0A0A0]/30 bg-[#A0A0A0]/5";
  if (status.toLowerCase().includes("ready") || status.toLowerCase().includes("rtmi"))
    return "text-emerald-400 border-emerald-400/30 bg-emerald-400/5";
  if (status.toLowerCase().includes("new") || status.toLowerCase().includes("launch"))
    return "text-[#F4D98B] border-[#F4D98B]/30 bg-[#F4D98B]/5";
  return "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/5";
};

// ─── Single Property Card ───
const PropertyCard = ({ prop, idx }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeUnit, setActiveUnit] = useState(0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="border border-[#D4AF37]/10 bg-[#0a0900] overflow-hidden"
      style={{ boxShadow: "0 0 60px rgba(212,175,55,0.04)" }}
    >
      {/* ── Hero Image ── */}
      <div className="relative h-80 md:h-[420px] overflow-hidden">
        <img
          src={prop.image}
          alt={prop.title}
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          style={{ filter: "brightness(0.65) saturate(0.8)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/90 via-[#080808]/30 to-transparent" />

        {/* Status + Tag */}
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          <span className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border font-heading ${statusColor(prop.status)}`}>
            {prop.status}
          </span>
          {prop.is_real_listing && (
            <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border text-[#D4AF37] border-[#D4AF37]/30 bg-black/50 font-heading">
              Verified Listing
            </span>
          )}
        </div>

        {/* Virtual Tour badge */}
        {prop.virtual_tour && (
          <a href={prop.virtual_tour} target="_blank" rel="noopener noreferrer"
            className="absolute top-4 right-4 flex items-center gap-2 text-[10px] tracking-wider uppercase px-3 py-1.5 border border-white/20 text-white bg-black/50 backdrop-blur-sm hover:border-[#D4AF37]/50 transition-all">
            <Eye size={10} /> Virtual Tour
          </a>
        )}

        {/* Bottom info */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase font-heading mb-2">{prop.developer}</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#FAF8F3] font-light mb-2">{prop.title}</h2>
          <div className="flex items-center gap-2 text-[#A0A0A0] text-sm">
            <MapPin size={12} className="text-[#D4AF37]" />
            <span>{prop.location}</span>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="p-6 md:p-8">
        {/* Quick Stats Row */}
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-8 border-b border-white/5 pb-8">
          {[
            { icon: Home, label: "Configurations", value: prop.configurations?.join(", ") || "—" },
            { icon: Layers, label: "Carpet Area", value: prop.carpet_area || "—" },
            { icon: Calendar, label: "Possession", value: prop.possession?.split("|")[0]?.trim() || "—" },
            { icon: Star, label: "Starting Price", value: prop.price_display?.split("–")[0]?.trim() || "—" },
            { icon: BadgeCheck, label: "ROI Score", value: `${prop.roi_score}/10` },
          ].map((s) => (
            <div key={s.label} className="border border-white/5 p-3 text-center">
              <s.icon size={12} className="text-[#D4AF37] mx-auto mb-2" />
              <p className="font-display text-base text-[#FAF8F3]">{s.value}</p>
              <p className="text-[9px] text-[#444] tracking-wider uppercase mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Pricing Table */}
        {prop.unit_plans && prop.unit_plans.length > 0 && (
          <div className="mb-8">
            <p className="text-[10px] tracking-[0.2em] text-[#D4AF37] uppercase mb-4 font-heading">Pricing & Floor Plans</p>
            {/* BHK Tabs */}
            <div className="flex flex-wrap gap-2 mb-5">
              {prop.unit_plans.map((u, i) => (
                <button key={i} onClick={() => setActiveUnit(i)}
                  className={`px-5 py-2 text-xs tracking-wider uppercase border transition-all duration-200 font-heading ${
                    activeUnit === i
                      ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                      : "border-white/10 text-[#555] hover:border-white/20"
                  }`}>
                  {prop.unit_plans[i].type}
                </button>
              ))}
            </div>
            {/* Units table */}
            <div className="border border-[#D4AF37]/10">
              <div className="grid grid-cols-3 border-b border-white/5 bg-[#0d0b00]">
                <div className="p-3 text-[9px] tracking-[0.2em] text-[#555] uppercase">Saleable Area (sq ft)</div>
                <div className="p-3 text-[9px] tracking-[0.2em] text-[#555] uppercase border-l border-white/5">Carpet Area (sq ft)</div>
                <div className="p-3 text-[9px] tracking-[0.2em] text-[#555] uppercase border-l border-white/5">Price</div>
              </div>
              {prop.unit_plans[activeUnit]?.units?.map((u, i) => (
                <div key={i}
                  className={`grid grid-cols-3 border-b border-white/5 transition-colors hover:bg-[#D4AF37]/[0.02] ${
                    i === prop.unit_plans[activeUnit].units.length - 1 ? "border-b-0" : ""
                  }`}>
                  <div className="p-3 text-sm text-[#FAF8F3] font-light">{u.sa}</div>
                  <div className="p-3 text-sm text-[#FAF8F3] font-light border-l border-white/5">{u.ca}</div>
                  <div className="p-3 text-sm text-[#D4AF37] font-display border-l border-white/5">₹{u.price}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        <p className="text-[#A0A0A0] text-sm leading-relaxed mb-8 border-l-2 border-[#D4AF37]/30 pl-4">
          {prop.description}
        </p>

        {/* Highlights */}
        {prop.highlights && prop.highlights.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {prop.highlights.map((h, i) => (
              <span key={i} className="flex items-center gap-1.5 text-[11px] text-[#A0A0A0] border border-white/10 px-3 py-1.5 tracking-wide">
                <CheckCircle size={10} className="text-[#D4AF37]" /> {h}
              </span>
            ))}
          </div>
        )}

        {/* Expandable: Amenities + RERA + Phases */}
        <button onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-xs text-[#D4AF37] tracking-wider uppercase mb-5 hover:text-[#F4D98B] transition-colors">
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {expanded ? "Hide" : "Show"} Amenities & Details
        </button>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.4 }}
              className="overflow-hidden"
            >
              {/* Amenities */}
              {prop.amenities && prop.amenities.length > 0 && (
                <div className="mb-6">
                  <p className="text-[10px] tracking-[0.2em] text-[#555] uppercase mb-4">Amenities</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {prop.amenities.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-[#A0A0A0]">
                        <div className="w-1 h-1 bg-[#D4AF37] rounded-full shrink-0" />
                        {a}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Phases (Sai World Empire) */}
              {prop.phases && prop.phases.length > 0 && (
                <div className="mb-6">
                  <p className="text-[10px] tracking-[0.2em] text-[#555] uppercase mb-4">Phase Details</p>
                  <div className="space-y-2">
                    {prop.phases.map((ph, i) => (
                      <div key={i} className="border border-white/5 p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div>
                          <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Phase</p>
                          <p className="text-sm text-[#FAF8F3]">{ph.name}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Towers</p>
                          <p className="text-xs text-[#A0A0A0]">{ph.towers?.join(", ")}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Possession</p>
                          <p className={`text-xs ${ph.possession?.includes("2024") ? "text-emerald-400" : "text-[#D4AF37]"}`}>{ph.possession}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RERA */}
              {prop.rera && (
                <div className="border border-[#D4AF37]/10 p-4 mb-6 flex items-start gap-3">
                  <Shield size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] tracking-[0.2em] text-[#D4AF37] uppercase mb-1">RERA Registration</p>
                    <p className="text-xs text-[#A0A0A0]">{prop.rera}</p>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Action Row ── */}
        <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-white/5">
          {/* WhatsApp CTA — primary */}
          <a href={waLink(prop.title)} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#25D366] text-white text-xs font-heading tracking-widest uppercase px-6 py-3.5 hover:bg-[#1da851] transition-colors"
          >
            <MessageCircle size={14} />
            Enquire on WhatsApp
          </a>

          {/* Map link */}
          {prop.map_link && (
            <a href={prop.map_link} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 border border-white/15 text-[#A0A0A0] text-xs font-heading tracking-wider uppercase px-5 py-3.5 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-all">
              <MapPin size={13} />
              View on Map
            </a>
          )}

          {/* Virtual Tour */}
          {prop.virtual_tour && (
            <a href={prop.virtual_tour} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 border border-white/15 text-[#A0A0A0] text-xs font-heading tracking-wider uppercase px-5 py-3.5 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-all">
              <Eye size={13} />
              Virtual Tour
            </a>
          )}

          {/* Score pill */}
          <div className="ml-auto flex items-center gap-2 border border-[#D4AF37]/20 px-4 py-2">
            <Star size={11} className="text-[#D4AF37]" />
            <span className="text-xs text-[#D4AF37] font-display">{prop.roi_score}/10</span>
            <span className="text-[9px] text-[#555] tracking-wider uppercase">Investment Score</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ─── WhatsApp Contact Form (bottom) ───
const WhatsAppContactForm = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [interest, setInterest] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `Hi%2C%20I%20am%20*${encodeURIComponent(name)}*%20(${encodeURIComponent(phone)}).%20I%20am%20interested%20in%20*${encodeURIComponent(interest || "a property")}*.%20${encodeURIComponent(message || "Please contact me.")}`;
    window.open(`https://wa.me/${WHATSAPP}?text=${text}`, "_blank");
  };

  return (
    <section className="py-20 border-t border-[#D4AF37]/10" style={{ background: "linear-gradient(180deg, #080808 0%, #0a0900 100%)" }}>
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-10">
          <motion.p {...fadeUp} className="section-label">Direct Contact</motion.p>
          <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-3xl md:text-4xl text-[#FAF8F3] font-light">
            Connect Directly on <span className="text-gold-gradient italic">WhatsApp</span>
          </motion.h2>
          <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-[#555] text-sm mt-3">
            Fill your details and we'll redirect you to WhatsApp with your enquiry pre-filled.
          </motion.p>
        </div>

        <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="glass-gold p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-[10px] tracking-[0.2em] text-[#D4AF37] uppercase mb-2 block">Your Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                  className="input-luxury" placeholder="Full Name" required />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] text-[#D4AF37] uppercase mb-2 block">Phone Number</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="input-luxury" placeholder="+91 XXXXX XXXXX" required />
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] text-[#D4AF37] uppercase mb-2 block">Property of Interest</label>
              <select value={interest} onChange={(e) => setInterest(e.target.value)}
                className="input-luxury">
                <option value="">Select a Property</option>
                <option>Sai World Empire – Kharghar</option>
                <option>Codename Waterfall – Kharghar</option>
                <option>Saras Icon – Seawoods</option>
                <option>Any / Multiple Properties</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] text-[#D4AF37] uppercase mb-2 block">Your Message (Optional)</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                className="input-luxury resize-none h-24"
                placeholder="Budget, timeline, preferred BHK configuration..." />
            </div>
            <button type="submit"
              className="w-full flex items-center justify-center gap-3 bg-[#25D366] text-white text-xs font-heading tracking-widest uppercase py-4 hover:bg-[#1da851] transition-colors">
              <MessageCircle size={16} />
              Send Enquiry via WhatsApp
              <ArrowRight size={14} />
            </button>
          </form>

          {/* Direct call strip */}
          <div className="mt-6 border-t border-white/5 pt-5 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-[#555] text-xs">Prefer to call directly?</p>
            <a href="tel:+917977009190"
              className="flex items-center gap-2 text-[#D4AF37] text-sm font-heading tracking-wider hover:text-[#F4D98B] transition-colors">
              <Phone size={14} />
              +91 7977009190
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ─── Main Page ───
const FeaturedListings = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/properties`);
        // Show real listings first, then others
        const real = res.data.filter((p) => p.is_real_listing);
        setListings(real.length > 0 ? real : res.data.slice(0, 3));
      } catch {
        setListings([]);
      }
      setLoading(false);
    };
    fetch();
  }, []);

  return (
    <div className="bg-[#080808] min-h-screen pt-24">
      {/* Page Header */}
      <div className="py-16 border-b border-white/5 relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, #0a0900 0%, #080808 100%)" }}>
        <div className="absolute inset-0 opacity-[0.025]"
          style={{ backgroundImage: "radial-gradient(circle, rgba(212,175,55,1) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.p {...fadeUp} className="section-label">Exclusive Listings</motion.p>
          <motion.h1 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#FAF8F3] mt-3 mb-4">
            Featured Properties<br />
            <span className="text-gold-gradient italic">by Sneha Reality</span>
          </motion.h1>
          <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-[#666] text-sm max-w-xl">
            Handpicked, verified real estate opportunities in Navi Mumbai's fastest-growing corridors. Detailed pricing, floor plans, amenities and direct WhatsApp access for every listing.
          </motion.p>

          {/* Breadcrumb */}
          <motion.div {...fadeUp} transition={{ delay: 0.3 }} className="flex items-center gap-2 mt-6 text-[11px] text-[#444] tracking-wider">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/properties" className="hover:text-[#D4AF37] transition-colors">Properties</Link>
            <span>/</span>
            <span className="text-[#D4AF37]">Featured Listings</span>
          </motion.div>
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="w-10 h-10 border border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-24">
            <Building2 size={32} className="text-[#D4AF37]/30 mx-auto mb-4" />
            <p className="text-[#555]">No listings available at the moment.</p>
          </div>
        ) : (
          <div className="space-y-12">
            {listings.map((prop, idx) => (
              <PropertyCard key={prop.id} prop={prop} idx={idx} />
            ))}
          </div>
        )}

        {/* All Properties CTA */}
        <div className="text-center mt-16 border-t border-white/5 pt-12">
          <p className="text-[#555] text-sm mb-6">Looking for more options across Navi Mumbai?</p>
          <Link to="/properties" className="btn-gold inline-flex items-center gap-2">
            Browse All Properties <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* WhatsApp Contact */}
      <WhatsAppContactForm />
    </div>
  );
};

export default FeaturedListings;
